/**
 * client/render/lighting.ts — pure (no three.js, no DOM) lighting constants and CPU mirrors of the
 * shader math, so they can be unit tested. OWNER: RENDER agent.
 *
 * Sources:
 *  [MAP]   research/map-hijacked.md (lightmap decode, sky rotation, fog, sun)
 *  [META]  game/public/assets/maps/<map>/meta.json (lighting / fog / sky / vision blocks)
 *  [SHD]   extracted/map-hijacked/shaders/*.asm (ps_pimp_shader_lmap_*, vs_pimp_shader_skycubemaphdr_*)
 *
 * Space: these helpers take CoD-space vectors (Z up) unless a name says `three`.
 */

/** meta.json fog block (sunParse.initWorldFog) [MAP "Result" table]. */
export interface FogParams {
  baseDist: number;
  halfDist: number;
  baseHeight: number;
  halfHeight: number;
  sunFogPitch: number;
  sunFogYaw: number;
  sunFogInner: number;
  sunFogOuter: number;
  fogColor: number[];
  fogOpacity: number;
  sunFogColor: number[];
  sunFogOpacity: number;
}

/**
 * Final output exposure: out = LUT(sqrt(col * EXPOSURE_K)) [MAP lightmap decode: `out = sqrt(lerp(fog, col,
 * fogFactor) * cb0[20].x)`]. cb0[20].x is a runtime constant that was not recovered.
 * ESTIMATE: 2^-exposure with the map's sun `exposure` 3.25 (sunParse.initWorldSun.exposure), times a
 * by-eye calibration factor (see EXPOSURE_CALIBRATION) chosen so the sunlit white hull/teak deck match the
 * loading screen art and the BO2 look. Tunable at runtime with window.__render.setExposure().
 */
export const EXPOSURE_CALIBRATION = 1.0; // ESTIMATE: 1 = pure 2^-exposure; matched by eye to the loading screen (warm cream hull, teak deck)
export function exposureScale(sunExposure: number): number {
  return Math.pow(2, -sunExposure) * EXPOSURE_CALIBRATION;
}

/** World lightmap irradiance scale: E = lightmap.png * 4 (export_map.py lm_scale) [META lighting.lightmapScale]. */
export const DEFAULT_LIGHTMAP_SCALE = 4;

/** AnglesToForward(pitch, yaw) in CoD space (positive pitch looks down). */
export function anglesToForward(out: number[], pitchDeg: number, yawDeg: number): number[] {
  const p = (pitchDeg * Math.PI) / 180, y = (yawDeg * Math.PI) / 180;
  const cp = Math.cos(p);
  out[0] = cp * Math.cos(y);
  out[1] = cp * Math.sin(y);
  out[2] = -Math.sin(p);
  return out;
}

/**
 * Sky cubemap lookup direction [MAP "Sky"; vs_pimp_shader_skycubemaphdr_2306aa25]:
 *   v = (dot(p.xy, rot.yz), dot(p.xy, rot.xy), +p.z),  rot = material skyBoxRotationSize.xyz
 * i.e. a 220 deg rotation about Z. The sign of z was resolved by locating the painted sun: with +p.z the
 * light direction lands on face 3 (-Y) at texel (187, 160), the painted sun is at (192, 218) — same azimuth;
 * its 8 deg elevation matches the lens-flare fx at cod (54062, 41345, 11412) (9.5 deg), the light itself is
 * at 20 deg (artistic offset in the original data). The result indexes a D3D cubemap (faces +X,-X,+Y,-Y,+Z,-Z),
 * whose face/texel convention is identical to GL's, so the vector is used as-is with textureCube.
 */
export function skyLookupDir(out: number[], p: ArrayLike<number>, rot: ArrayLike<number>): number[] {
  const x = p[0] * rot[1] + p[1] * rot[2];
  const y = p[0] * rot[0] + p[1] * rot[1];
  out[0] = x; out[1] = y; out[2] = p[2];
  return out;
}

/** GL/D3D cube face + texel (u right, v down, in [0,1]) for a lookup vector; used by tests and debug. */
export function cubeFaceUV(v: ArrayLike<number>): { face: number; u: number; v: number } {
  const x = v[0], y = v[1], z = v[2];
  const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
  let face: number, sc: number, tc: number, ma: number;
  if (ax >= ay && ax >= az) { ma = ax; face = x > 0 ? 0 : 1; sc = x > 0 ? -z : z; tc = -y; }
  else if (ay >= az) { ma = ay; face = y > 0 ? 2 : 3; sc = x; tc = y > 0 ? z : -z; }
  else { ma = az; face = z > 0 ? 4 : 5; sc = z > 0 ? x : -x; tc = -y; }
  return { face, u: (sc / ma + 1) / 2, v: (tc / ma + 1) / 2 };
}

/**
 * Fog transmittance-weighted amount for one view ray (mirror of GLSL `bo2Fog`). ESTIMATE on the exact
 * T6 formula (the fog shader constants are runtime): IW-style exponential fog with a half distance and an
 * exponential height falloff:
 *   density(z) = min(1, 2^(-(z - baseHeight) / halfHeight))
 *   amount     = opacity * (1 - 2^(-max(0, dist - baseDist) / halfDist * avgDensity(eyeZ..pointZ)))
 * Returns [amount 0..1, sunFogBlend 0..1]; colour = mix(fogColor, sunFogColor, sunFogBlend).
 * Sun fog blend: 1 inside `sunFogInner`, 0 beyond `sunFogOuter` (degrees from the sun-fog direction), linear.
 */
export function fogAmount(f: FogParams, eye: ArrayLike<number>, point: ArrayLike<number>, out: number[] = [0, 0]): number[] {
  const dx = point[0] - eye[0], dy = point[1] - eye[1], dz = point[2] - eye[2];
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const avgDensity = avgHeightDensity(f, eye[2], point[2]);
  const fd = Math.max(0, dist - f.baseDist) / f.halfDist;
  const sunDir = anglesToForward([0, 0, 0], f.sunFogPitch, f.sunFogYaw);
  const cosA = dist > 0 ? (dx * sunDir[0] + dy * sunDir[1] + dz * sunDir[2]) / dist : 1;
  const ang = (Math.acos(Math.max(-1, Math.min(1, cosA))) * 180) / Math.PI;
  const span = Math.max(1e-3, f.sunFogOuter - f.sunFogInner);
  const sunBlend = Math.max(0, Math.min(1, 1 - (ang - f.sunFogInner) / span));
  const opacity = f.fogOpacity + (f.sunFogOpacity - f.fogOpacity) * sunBlend;
  out[0] = opacity * (1 - Math.pow(2, -fd * avgDensity));
  out[1] = sunBlend;
  return out;
}

export function avgHeightDensity(f: FogParams, z0: number, z1: number): number {
  const k = Math.LN2 / f.halfHeight;
  const rho = (z: number) => Math.min(1, Math.pow(2, -(z - f.baseHeight) / f.halfHeight));
  const dz = z1 - z0;
  if (Math.abs(dz) < 1 || (z0 <= f.baseHeight && z1 <= f.baseHeight)) return rho((z0 + z1) / 2);
  // closed form above baseHeight; below it density is clamped to 1 (approximated by the mean of the ends
  // when the ray crosses baseHeight — at Hijacked's 10702 half height the difference is < 0.1%)
  if (z0 < f.baseHeight || z1 < f.baseHeight) return (rho(z0) + rho(z1)) / 2;
  return (rho(z0) - rho(z1)) / (k * dz);
}
